import { P2GBox } from '@parcel2go/core-ui';
import { P2GButton } from '@parcel2go/core-ui';
import { P2GDropdown } from '@parcel2go/core-ui';
import { SvgEnquiryMoreInfo } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';

import { useEffect } from 'react';
import { useState } from 'react';
import {
  ConsignmentParcelApiModel,
  ConsignmentParcelItemApiModel,
  Dimensions,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { GenerateNumbers } from '../../../lib/helpers/ArrayHelper';
import { uid } from '../../../lib/helpers/uid';
import AlertWithIcon from '../../AlertWithIcon';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { GenerateSplitSummary } from './GroupingHelper';
import { GroupingPanelParcels } from './GroupingPanelParcels';
import { QuantityValueSummary } from '../panels/QuantityValueSummary';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

export interface props {
  group: SmartSendItemGroup;
  split: (item: SmartSendItemApiModel, newItems: SmartSendItemApiModel[]) => void;
}

export const Splitting: React.FC<props> = (props) => {
  const [split, setSplit] = useState(2);
  const [dims] = useState<Dimensions>({ ...props.group.Items[0].PackageDimensions });
  const [parcels, setParcels] = useState<ConsignmentParcelApiModel[]>([]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  useEffect(() => {
    const newParcels: ConsignmentParcelApiModel[] = [];

    const item = props.group.Items[0];

    for (let index = 0; index < split; index++) {
      newParcels.push({
        CanAdd: false,
        Dimensions: { ...dims },
        Id: item.Id,
        UID: uid(),
        Orders: item.MarketplaceOrderLines.map((x) => {
          const s = QuantityValueSummary(x.Value, split);
          const cpi: ConsignmentParcelItemApiModel = {
            ...x,
            Id: null,
            UID: uid(),
            Description: x.Product.Description,
            Value: index == 0 ? s.valueForFirstItem : s.valueForOtherItems
          };
          return cpi;
        })
      });
    }
    setParcels(newParcels);
  }, [split]);

  function createSplitItems() {
    const summary = GenerateSplitSummary(
      props.group,
      split,
      parcels.map((x) => x.Orders[0].Description),
      dims
    );
    if (summary) {
      props.split(summary.item, summary.newItems);
    }
  }

  function setDesciption(uid: string, desc: string) {
    const existing = [...parcels];

    existing.forEach((p) => {
      p.Orders.forEach((o) => {
        if (o.UID === uid) {
          o.Description = desc;
        }
      });
    });

    setParcels(existing);
  }

  const currencyCode = props.group.Items[0]?.CurrencyCode;

  return (
    <P2GBox>
      <SidePanelSections>
        <P2GBox>
          <P2GText>
            <Trans
              t={t}
              i18nKey="SEND_IN_MULTIPLE_PARCELS_TEXT"
              defaults={`Does this item need to be sent in multiple parcels?
            <br /> If so, choose how many parcels below.`}
            />
          </P2GText>
        </P2GBox>
        <P2GBox>
          <P2GDropdown
            id="Total Shipments"
            label={t('NUMBER_OF_PARCELS_DROPDOWN_LABEL', { defaultValue: 'Number of Parcels' })}
            options={GenerateNumbers(2, 10).map((x) => {
              return { value: x.toString() };
            })}
            value={split as any}
            onChange={(v) => setSplit(v as any)}
          />
        </P2GBox>

        <SidePanelSection title={t('SHIPMENT_SUMMARY_SUBTITLE', { defaultValue: 'Shipment Summary' })}>
          <GroupingPanelParcels parcels={parcels} updateDescription={setDesciption} currencyCode={currencyCode} />
        </SidePanelSection>

        <AlertWithIcon color="info" icon={<SvgEnquiryMoreInfo />}>
          <P2GText variant="body2">
            {t('DIMENSION_REMINDER_MESSAGE', {
              defaultValue:
                'Remember to check the weight and dimensions of your split parcels as these are likely to be smaller.'
            })}
          </P2GText>
        </AlertWithIcon>
      </SidePanelSections>

      <P2GBox mt={2}>
        <P2GButton disabled={split <= 1} onClick={createSplitItems}>
          {t('CONFIRM_CREATE_BUTTON', { defaultValue: 'Confirm and Create' })}
        </P2GButton>
      </P2GBox>
    </P2GBox>
  );
};
